import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import Card from "components/card";
import { DEPARTMENT_COLORS } from "variables/constants";
import { Clock, MessageCircle, Send, X } from "lucide-react";
import Button from "components/button";
import { Form } from "antd";
import FormItem from "components/fields/FormItem";
import TextArea from "antd/es/input/TextArea";
import { 
    getTicketReplyList, 
    doTicketReplyCreate 
} from "../../../../../../redux/ticketreply/ticketreplyState";
import {
    doTicketUpdate
} from "../../../../../../redux/supportticket/supportticketState";

export const TKSSTAT_IP = 143;
export const TKSSTAT_RES = 144;
export const DEPT_PS = 171;

const MessageCardDetails = ({
    id,
    initial,
    department,
    title,
    priority,
    status,
    date,
    time,
    fullMessage,
    replyList,
    replyListRequesting,
    getReplies,
    doCreateReply,
    currentUserId,
    createdBy,
    doTicketUpdate,
    deptId,
    priorityId,
    categoryId,
    titleId,
}) => {
    const [showReplyBox, setShowReplyBox] = useState(false);
    const [markAsResolved, setMarkAsResolved] = useState(false);
    const [form] = Form.useForm();

    const isMessageDepartment = deptId === DEPT_PS;
    
    const replies = replyList || [];
    const loadingReplies = replyListRequesting;
    const isTicketCreator = currentUserId === createdBy?.id;
    const isDepartmentMember = !isTicketCreator;
    const isResolved = status === 'resolved';
    
    const sortedReplies = [...replies].sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
    );
    
    const deptColors = DEPARTMENT_COLORS[department] || { bg: "bg-gray-100", icon: "text-gray-600" };
    
    useEffect(() => {
        if (id) {
            getReplies({ ticketId: id});
        }
    }, [id, getReplies]);

    const onSubmit = (formData) => {
        const message = formData?.trp_message;
        
        if (!message || !message.trim()) {
            console.warn('No message to send');
            return;
        }

        doCreateReply({
            trp_ticket_id: id,
            trp_user_id: currentUserId,
            trp_message: message,
            trp_is_internal: isDepartmentMember ? 0 : 1,
            trp_seen: 0,
            trp_status_id: null
        });

        let newStatusId = null;
        if (isDepartmentMember) {
            if (markAsResolved) {
                newStatusId = TKSSTAT_RES;
            } else if (status === 'pending') {
                newStatusId = TKSSTAT_IP;
            }
        }

        if (newStatusId) {
            doTicketUpdate({
                tic_id: id,
                tic_title: title,
                tic_description: fullMessage,
                tic_priority_id: priorityId,    
                tic_department_id: deptId,
                tic_category_id: categoryId,
                tic_title_id: titleId,
                tic_status_id: newStatusId,
                modified_by: currentUserId
            });
        }

        setTimeout(() => {
            getReplies({ ticketId: id });
        }, 500);
        
        form.resetFields();
        setMarkAsResolved(false);
        setShowReplyBox(false);
    };

    const getReplyButtonText = () => {
        if (isTicketCreator) {
            return `Reply to ${department}`;
        }
        return 'Reply to Customer';
    };

    const canReply = () => {
        if (isResolved) return false;
        
        if (isDepartmentMember) return true;
        
        return replies.length > 0;
    };

    return (
        <Card className="lg:col-span-2">
            <div className="opacity-100 transform-none">
                <div className="bg-white text-slate-900 flex flex-col gap-6 rounded-xl border-2 border-emerald-500 shadow-xl overflow-hidden dark-mode-card">
                    <div className="grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 [.border-b]:pb-6 bg-gradient-to-br from-emerald-50 to-green-50 border-b p-3 sm:p-5 dark:from-emerald-900 dark:to-green-900">
                        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                            <span className={`relative items-center justify-center flex size-full shrink-0 overflow-hidden rounded-full w-10 h-10 sm:w-11 sm:h-11 border-2 border-white shadow-sm text-[11px] sm:text-[13px] font-semibold ${deptColors.bg} ${deptColors.icon}`}>
                                {initial}
                            </span>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-[14px] sm:text-[16px] mb-1 line-clamp-2 title-text">
                                    {title}
                                </h4>
                                <p className="text-[11px] sm:text-[12px] truncate muted-text">
                                    To: 
                                    <span className="ml-1 font-medium">{department}</span>
                                </p>
                                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                    {!isMessageDepartment && (
                                        <span className={`status-indicator ${status?.toLowerCase()} text-[9px] uppercase`}>
                                            {status}
                                        </span>
                                    )}
                                    <span className={`status-indicator ${priority?.toLowerCase().replace(/\s+/g, "-")} text-[9px] uppercase`}>
                                        {priority} PRIORITY
                                    </span>
                                    <span className="text-[9px] sm:text-[10px] flex items-center gap-1 tracking-wide muted-text">
                                        <Clock className="w-3 h-3"/> {date} at {time}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="pb-6 p-3 sm:p-5">
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="p-1.5 rounded-full bg-blue-100">
                                    <Send className="w-3.5 h-3.5 text-blue-600" />
                                </div>
                                <h4 className="text-[13px] font-semibold title-text">
                                    {isTicketCreator ? "Your Message" : "Customer Message"}
                                </h4>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 dark-mode-card">
                                <div className="whitespace-pre-wrap text-[12px] sm:text-[13px] leading-relaxed text-slate-800 title-text">
                                    {fullMessage}
                                </div>
                            </div>
                        </div>
                        
                        {loadingReplies && (
                            <div className="text-center py-4">
                                <div className="animate-spin inline-block w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full"></div>
                                <span className="ml-2 text-sm text-slate-500">Loading replies...</span>
                            </div>
                        )}
                        
                        {!loadingReplies && replies.length === 0 && isTicketCreator && !isMessageDepartment &&(
                            <div className="mt-6 p-4 rounded-lg bg-orange-50 border border-orange-200 dark:bg-orange-800">
                                <div className="flex items-start gap-2">
                                    <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5 dark:text-orange-500" />
                                    <div>
                                        <p className="text-[12px] font-semibold text-orange-900 mb-1 dark:text-orange-50">
                                            Awaiting Response
                                        </p>
                                        <p className="text-[11px] text-orange-700 dark:text-orange-200">
                                            The {department} team will respond within 24-48 hours. You'll receive a notification when they reply.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {!loadingReplies && sortedReplies?.length > 0 && (
                            <div>
                                {sortedReplies.map((reply, index) => {
                                    const isCustomerReply = reply.trp_user_id === createdBy?.id;
                                    const isCurrentUserReply = reply.trp_user_id === currentUserId;
                                    
                                    let replyLabel = '';
                                    if (isCurrentUserReply && isCustomerReply) {
                                        replyLabel = 'Your Reply'; 
                                    } else if (isCurrentUserReply && !isCustomerReply) {
                                        replyLabel = 'Your Response';  
                                    } else if (isCustomerReply) {
                                        replyLabel = 'Customer Reply'; 
                                    } else {
                                        replyLabel = 'Department Response';  
                                    }
                                    
                                    return (
                                        <React.Fragment key={index}>
                                            <div className="bg-slate-200 shrink-0 h-px w-full my-6"></div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className={`p-1.5 rounded-full ${
                                                        isCustomerReply ? 'bg-purple-100' : 'bg-green-100'
                                                    }`}>
                                                        <MessageCircle className={`w-3.5 h-3.5 ${
                                                            isCustomerReply ? 'text-purple-600' : 'text-green-600'
                                                        }`} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h4 className="text-[13px] font-semibold">
                                                            {replyLabel}
                                                        </h4>
                                                        <p className="text-[10px] text-slate-700 tracking-normal">
                                                            {reply?.created_date} at {reply?.created_time}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className={`rounded-lg p-4 border-2 ${
                                                    isCustomerReply 
                                                        ? 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200'
                                                        : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
                                                }`}>
                                                    <div className="text-[12px] sm:text-[13px] leading-relaxed text-slate-800">
                                                        {reply.trp_message}
                                                    </div>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )}
                        
                        <div>
                            <div className="bg-slate-200 shrink-0 h-px w-full my-6"></div>
                            
                            {isResolved && (
                                <div className="text-center py-4">
                                    <p className="text-[12px] text-gray-600">
                                        This ticket has been resolved. No further replies are allowed.
                                    </p>
                                </div>
                            )}
                            
                            {!showReplyBox && canReply() && (
                                <div className="flex justify-center">
                                    <Button
                                        onClick={() => setShowReplyBox(true)}
                                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-all shrink-0 text-white hover:bg-emerald-500/90 h-9 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 hover:shadow-none gap-2"
                                    >
                                        <MessageCircle className="w-4 h-4" />
                                        {getReplyButtonText()}
                                    </Button>
                                </div>
                            )}
                            
                            {showReplyBox && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-[13px] font-semibold">
                                            Compose Reply
                                        </h4>
                                        <Button
                                            onClick={() => {
                                                setShowReplyBox(false);
                                                setMarkAsResolved(false);
                                                form.resetFields();
                                            }}
                                            className="inline-flex items-center justify-center whitespace-nowrap transition-all shrink-0 outline-none hover:bg-slate-100 hover:text-slate-900 h-8 hover:shadow-none rounded-md gap-1.5 px-2.5"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    
                                    <Form
                                        form={form}
                                        layout="vertical" 
                                        onFinish={onSubmit}
                                    >
                                        <FormItem 
                                            name="trp_message"
                                            rules={[{ required: true, message: 'Please enter your reply' }]}
                                        >
                                            <TextArea
                                                className="input-field"
                                                maxLength={1000}
                                                placeholder="Type your reply here..."
                                                rows={2}
                                            />
                                        </FormItem>
                                    </Form>
                                    
                                    {isDepartmentMember && !isResolved && (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="markAsResolved"
                                                checked={markAsResolved}
                                                onChange={(e) => setMarkAsResolved(e.target.checked)}
                                                className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                            />
                                            <label htmlFor="markAsResolved" className="text-[11px] text-slate-700 cursor-pointer">
                                                Mark this ticket as resolved after sending reply
                                            </label>
                                        </div>
                                    )}
                                    
                                    <div className="flex gap-2 justify-end">
                                        <Button
                                            onClick={() => {
                                                setShowReplyBox(false);
                                                setMarkAsResolved(false);
                                                form.resetFields();
                                            }}
                                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all shrink-0 bg-white text-slate-900 hover:bg-slate-100 hover:shadow-none h-9 px-4 py-2 border-2"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={() => form.submit()}
                                            className="inline-flex items-center justify-center gap-2 rounded-xl text-sm font-medium transition-all outline-none hover:bg-emerald-500/90 h-9 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:shadow-none hover:from-emerald-700 hover:to-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Send className="w-4 h-4 mr-2"/>
                                            Send Reply
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

const mapStateToProps = (state) => ({
    replyList: state.ticketReply.ticketReplyList?.items,
    replyListRequesting: state.ticketReply.ticketReplyList?.requesting,
    currentUserId: state.auth.user?.id,
    currentUserDepartment: state.auth.user?.department,  
});

const mapDispatchToProps = (dispatch) => ({
    getReplies: (filters) => dispatch(getTicketReplyList(filters)),
    doCreateReply: (data) => dispatch(doTicketReplyCreate(data)),
    doTicketUpdate: (data) => dispatch(doTicketUpdate(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(MessageCardDetails);